"use client";

import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS } from "../../../constants/config";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";

const FALLBACK_TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "students", label: "Students" },
  { id: "teachers", label: "Teachers" },
  { id: "staff", label: "Staff" },
  { id: "teacher-attendance", label: "Teacher Attendance" },
  { id: "staff-leave", label: "Leave Management" },
  { id: "staff-attendance", label: "My Attendance" },
  { id: "registration-faces", label: "Registration Faces" },
  { id: "payroll", label: "Payroll" },
  { id: "class-transition", label: "Class Transition" },
  { id: "classroom-mapping", label: "ClassRoom Mapping" },
  { id: "fee-management", label: "Fee Management" },
  { id: "bus-routes", label: "Bus Routes" },
  { id: "vendor", label: "Vendor" },
  { id: "assets", label: "Assets" },
  { id: "inventory", label: "Inventory" },
  { id: "announcements", label: "Announcements" },
  { id: "holiday", label: "Holiday" },
  { id: "alert", label: "Alert Management" },
  { id: "settings", label: "Settings (full)" },
  { id: "settings-classes", label: "Settings: Classes" },
  { id: "settings-sections", label: "Settings: Sections" },
  { id: "settings-subjects", label: "Settings: Subjects" },
  { id: "settings-academic-years", label: "Settings: Academic Years" },
  { id: "settings-receipt-customization", label: "Settings: Receipt" },
  { id: "settings-email-management", label: "Settings: Email" },
  { id: "settings-late-fee", label: "Settings: Late Fee" },
  { id: "settings-attendance-settings", label: "Settings: Attendance" },
  { id: "settings-admin", label: "Settings: Roles & Staff permissions" },
  { id: "settings-about", label: "Settings: About School" },
  { id: "faq", label: "FAQ" },
];

export function TeacherPermissionModal({ teacherId, teacherName, isOpen, onClose, onSuccess }) {
  const [permissions, setPermissions] = useState([]);
  const [tabs, setTabs] = useState(FALLBACK_TABS);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!teacherId) return;
    try {
      setLoading(true);
      const [permRes, listRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.teacherAccessPermissions(teacherId)),
        axiosInstance.get(API_ENDPOINTS.permissionsList).catch(() => ({ data: { data: { permissions: null } } })),
      ]);
      setPermissions(permRes.data.data.permissions || []);
      const list = listRes.data?.data?.permissions;
      if (Array.isArray(list) && list.length > 0) {
        setTabs(list.map((p) => ({ id: p.value, label: p.label || p.value })));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch permissions");
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    if (isOpen && teacherId) {
      setError(null);
      fetchPermissions();
    }
  }, [isOpen, teacherId, fetchPermissions]);

  const handlePermissionChange = (tabId) => {
    setPermissions((current) =>
      current.includes(tabId) ? current.filter((id) => id !== tabId) : [...current, tabId]
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      await axiosInstance.put(API_ENDPOINTS.teacherAccessPermissions(teacherId), { permissions });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-2">Extra tab access</h2>
        <p className="text-sm text-muted-foreground mb-4">{teacherName}</p>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {tabs.map((tab) => (
              <div key={tab.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`teacher-perm-${tab.id}`}
                  checked={permissions.includes(tab.id)}
                  onCheckedChange={() => handlePermissionChange(tab.id)}
                  disabled={loading}
                />
                <Label htmlFor={`teacher-perm-${tab.id}`}>{tab.label}</Label>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
